import { HttpErrorResponse, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, from, switchMap, throwError } from 'rxjs';
import { CustomerAuthService } from './customer-auth.service';
import { API_BASE } from './ordering-api';

/**
 * URLs whose 401s belong to the CUSTOMER session (profile, addresses, orders, coins, checkout).
 * Deliberately excludes the partner (/api/sales|company|hr|auth|…) and rider (/api/rider) scopes —
 * those interceptors run downstream with their own tokens and their own refresh handling, and a
 * partner/rider 401 bubbling back must never churn or clear the customer session.
 */
// Also covers the customer inbox (/api/notifications/inbox…) so its poll gets proactive+reactive token
// refresh like the other customer calls. Anchored to `inbox` so it does NOT match the partner staff
// inbox (/api/notifications/staff/inbox) or the rider inbox (/api/rider/notifications/inbox).
const CUSTOMER_SCOPE = /\/api\/(customer-auth|public)\/|\/api\/notifications\/inbox(\/|$|\?)/i;

/** The customer session endpoint — the only /customer-auth/ URL whose 401 means "token expired".
 *  All other auth URLs (login/otp/social/refresh/logout) 401 on bad credentials and must surface
 *  as-is (retrying them would recurse or mask the real error). */
const CUSTOMER_ME = /\/api\/customer-auth\/me(\/|$|\?)/i;

/**
 * Attaches the customer access token to our API calls when signed in. Handles both relative
 * ('/api/...') and absolute (API_BASE + '/api/...') URLs. No token → request passes unchanged.
 * For customer-scoped endpoints it mirrors the partner/ERP interceptors: PROACTIVELY refreshes an
 * already-expired access token before sending, refreshes REACTIVELY on a server 401 (one shared
 * in-flight refresh), and clears the dead session when the refresh also fails — the fix for
 * "checkout shows no name/phone/addresses after the tab sat open past token expiry".
 */
export const customerAuthInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(CustomerAuthService);
  const token = auth.accessToken;
  const isOurApi = req.url.startsWith('/api/') || (!!API_BASE && req.url.startsWith(`${API_BASE}/api/`));
  if (!token || !isOurApi) return next(req);

  const withToken = (r: HttpRequest<unknown> = req) =>
    r.clone({ setHeaders: { Authorization: `Bearer ${auth.accessToken!}` } });

  const refreshable = CUSTOMER_SCOPE.test(req.url)
    && (!req.url.toLowerCase().includes('/customer-auth/') || CUSTOMER_ME.test(req.url));
  if (!refreshable) return next(withToken());

  const send = () =>
    next(withToken()).pipe(
      catchError((err: HttpErrorResponse) => {
        // Reactive: an unexpected server 401 (clock skew / server-side revocation) → one refresh + replay.
        if (err.status === 401) {
          return from(auth.refreshAccessToken()).pipe(
            switchMap((ok) => {
              if (ok) return next(withToken());
              auth.clearSession(); // dead session — signals flip the UI to signed-out
              return throwError(() => err);
            }),
          );
        }
        return throwError(() => err);
      }),
    );

  // Proactive: a locally-expired access token is refreshed before the request goes out, so a
  // returning customer with a live refresh token never even sees a failed call.
  if (auth.isTokenExpired()) {
    return from(auth.refreshAccessToken()).pipe(
      switchMap((ok) => {
        if (ok) return send();
        auth.clearSession();
        return next(req); // fall through anonymously — public endpoints still work signed-out
      }),
    );
  }

  return send();
};
